const THUMBNAIL_TYPE = 'image/jpeg'
const THUMBNAIL_QUALITY = 0.92
const THUMBNAIL_MAX_WIDTH = 720
const THUMBNAIL_MAX_HEIGHT = 1280
const VIDEO_OPERATION_TIMEOUT_MS = 15000
const MAX_VIDEO_DURATION_SECONDS = 5 * 60
export const DEFAULT_THUMBNAIL_SEEK_TIME = 0.05

const getSafeSeekTime = (video: HTMLVideoElement, seekTo: number) => {
  if (!Number.isFinite(video.duration) || video.duration <= 0) {
    return seekTo
  }

  const latestFrameTime = Math.max(video.duration - 0.01, 0)

  return Math.min(Math.max(seekTo, 0), latestFrameTime)
}

const getVideoDimensions = (video: HTMLVideoElement) => {
  if (!video.videoWidth || !video.videoHeight) {
    throw new Error('Cannot read the selected video dimensions')
  }

  return {
    width: video.videoWidth,
    height: video.videoHeight,
  }
}

const validateVideoElement = (video: HTMLVideoElement) => {
  if (!Number.isFinite(video.duration) || video.duration <= 0) {
    throw new Error('Cannot read the selected video duration')
  }

  if (video.duration > MAX_VIDEO_DURATION_SECONDS) {
    throw new Error('Video must be 5 minutes or shorter')
  }

  getVideoDimensions(video)
}

type LoadedVideo = {
  video: HTMLVideoElement
  cleanup: () => void
}

type VideoPreload = 'auto' | 'metadata'

const loadVideoMetadata = (file: File, preload: VideoPreload = 'metadata') =>
  new Promise<LoadedVideo>((resolve, reject) => {
    const video = document.createElement('video')
    const url = URL.createObjectURL(file)
    let isSettled = false
    let isCleanedUp = false
    let timeoutId: number | undefined

    const cleanup = () => {
      if (isCleanedUp) {
        return
      }

      isCleanedUp = true

      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId)
      }

      video.onloadedmetadata = null
      video.onerror = null
      video.onabort = null
      URL.revokeObjectURL(url)
      video.removeAttribute('src')
      video.load()
    }

    const rejectOnce = (error: Error) => {
      if (isSettled) {
        return
      }

      isSettled = true
      cleanup()
      reject(error)
    }

    timeoutId = window.setTimeout(
      () => rejectOnce(new Error('Timed out while reading the selected video')),
      VIDEO_OPERATION_TIMEOUT_MS
    )

    video.preload = preload
    video.muted = true
    video.playsInline = true
    video.src = url

    video.onloadedmetadata = () => {
      if (isSettled) {
        return
      }

      try {
        validateVideoElement(video)
      } catch (error) {
        rejectOnce(error instanceof Error ? error : new Error('Cannot validate the selected video'))
        return
      }

      isSettled = true

      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId)
        timeoutId = undefined
      }

      video.onloadedmetadata = null
      video.onerror = null
      video.onabort = null
      resolve({ video, cleanup })
    }

    video.onerror = () => rejectOnce(new Error('Cannot read the selected video file'))
    video.onabort = () => rejectOnce(new Error('Reading the selected video was interrupted'))
  })

export const validateVideoMetadata = async (file: File) => {
  const { cleanup } = await loadVideoMetadata(file)

  cleanup()
}

export const extractPosterFromVideo = async (file: File) => {
  const { video, cleanup: cleanupVideo } = await loadVideoMetadata(file, 'auto')

  return new Promise<File>((resolve, reject) => {
    const canvas = document.createElement('canvas')
    let isSettled = false
    let isCapturing = false

    const cleanup = () => {
      window.clearTimeout(timeoutId)

      video.onloadeddata = null
      video.onseeked = null
      video.onerror = null
      video.onabort = null
      cleanupVideo()
    }

    const rejectOnce = (error: Error) => {
      if (isSettled) {
        return
      }

      isSettled = true
      cleanup()
      reject(error)
    }

    const resolveOnce = (thumbnail: File) => {
      if (isSettled) {
        return
      }

      isSettled = true
      cleanup()
      resolve(thumbnail)
    }

    const captureFrame = () => {
      if (isSettled || isCapturing) {
        return
      }

      isCapturing = true

      let dimensions: ReturnType<typeof getVideoDimensions>

      try {
        dimensions = getVideoDimensions(video)
      } catch (error) {
        rejectOnce(error instanceof Error ? error : new Error('Cannot read the selected video dimensions'))
        return
      }

      const { width: videoWidth, height: videoHeight } = dimensions
      const scale = Math.min(1, THUMBNAIL_MAX_WIDTH / videoWidth, THUMBNAIL_MAX_HEIGHT / videoHeight)

      canvas.width = Math.max(1, Math.round(videoWidth * scale))
      canvas.height = Math.max(1, Math.round(videoHeight * scale))

      const context = canvas.getContext('2d')

      if (!context) {
        rejectOnce(new Error('Cannot create thumbnail canvas'))
        return
      }

      try {
        context.drawImage(video, 0, 0, canvas.width, canvas.height)
      } catch (error) {
        rejectOnce(error instanceof Error ? error : new Error('Cannot capture the selected video frame'))
        return
      }

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            rejectOnce(new Error('Cannot extract thumbnail from the selected video'))
            return
          }

          resolveOnce(new File([blob], `${file.name.replace(/\.[^.]+$/, '')}-thumbnail.jpg`, { type: THUMBNAIL_TYPE }))
        },
        THUMBNAIL_TYPE,
        THUMBNAIL_QUALITY
      )
    }

    const timeoutId = window.setTimeout(
      () => rejectOnce(new Error('Timed out while extracting the video thumbnail')),
      VIDEO_OPERATION_TIMEOUT_MS
    )

    video.onerror = () => rejectOnce(new Error('Cannot read the selected video file'))
    video.onabort = () => rejectOnce(new Error('Thumbnail extraction was interrupted'))
    video.onseeked = captureFrame

    const safeSeekTime = getSafeSeekTime(video, DEFAULT_THUMBNAIL_SEEK_TIME)

    if (safeSeekTime === 0) {
      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        captureFrame()
      } else {
        video.onloadeddata = captureFrame
      }

      return
    }

    try {
      video.currentTime = safeSeekTime
    } catch (error) {
      rejectOnce(error instanceof Error ? error : new Error('Cannot seek the selected video'))
    }
  })
}
